import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../lib/ThemeContext';
import { dicebear, photoUrl } from '../utils/api';

const NAV_ITEMS = [
  { icon: '🔍', label: 'Discover',    path: '/mosaic' },
  { icon: '📅', label: 'Events',      path: '/meets'  },
  { icon: '💬', label: 'Chat',        path: null       },
  { icon: '🏆', label: 'Leaderboard', path: '/leaderboard' },
];

const THEME_OPTS = [
  { icon: '☀️', val: 'light',  label: 'Light'  },
  { icon: '🌙', val: 'dark',   label: 'Dark'   },
  { icon: '◑', val: 'system', label: 'System' },
];

export default function DesktopSidebar({ expanded, onToggle, onChatOpen, currentUser }) {
  const navigate          = useNavigate();
  const { pathname }      = useLocation();
  const { totalUnread }   = useSocket();
  const { logout }        = useAuth();
  const { theme, setTheme } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const COLLAPSED_W = 64;
  const EXPANDED_W  = 220;
  const w = expanded ? EXPANDED_W : COLLAPSED_W;

  const themeIdx   = THEME_OPTS.findIndex(t => t.val === theme);
  const cycleTheme = () => setTheme(THEME_OPTS[(themeIdx + 1) % 3].val);

  const navBtn = (style = {}) => ({
    display: 'flex', alignItems: 'center',
    justifyContent: expanded ? 'flex-start' : 'center',
    gap: 12, borderRadius: 10, border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-body)', fontSize: '0.875rem',
    textAlign: 'left', width: '100%', whiteSpace: 'nowrap',
    position: 'relative', transition: 'background 0.15s, color 0.15s',
    ...style,
  });

  return (
    <div style={{
      width: w, minWidth: w, height: '100dvh',
      background: 'var(--bg2, #111118)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column',
      transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
      overflow: 'hidden', flexShrink: 0, position: 'relative', zIndex: 10,
    }}>

      {/* ── Logo row ── */}
      <div style={{
        height: 64, padding: '0 10px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        {expanded && (
          <button
            onClick={() => navigate('/mosaic')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', flex: 1, minWidth: 0,
            }}
          >
            <img src="/logo.svg" alt="NxtLove" className="app-logo" style={{ height: '40px', width: 'auto', maxWidth: '100%' }} />
          </button>
        )}
        <button
          onClick={onToggle}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)',
            color: 'var(--text3)', cursor: 'pointer', borderRadius: 8,
            width: 36, height: 36, flexShrink: 0,
            marginLeft: expanded ? 0 : 'auto',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.8rem', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'var(--text3)'; }}
        >
          {expanded ? '◀' : '▶'}
        </button>
      </div>

      {/* ── Nav items ── */}
      <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        {NAV_ITEMS.map(item => {
          const isChat   = item.label === 'Chat';
          const isActive = item.path && (pathname === item.path || pathname.startsWith(item.path + '/'));
          return (
            <button
              key={item.label}
              onClick={() => isChat ? onChatOpen() : navigate(item.path)}
              aria-label={isChat && totalUnread > 0 ? `${item.label} (${totalUnread} unread)` : item.label}
              aria-current={isActive ? 'page' : undefined}
              title={!expanded ? item.label : undefined}
              style={navBtn({
                padding: expanded ? '10px 12px' : '10px',
                background: isActive
                  ? 'linear-gradient(135deg,var(--glow),rgba(255,140,105,0.10))'
                  : 'transparent',
                color: isActive ? 'var(--accent)' : 'var(--text2)',
                fontWeight: isActive ? 600 : 400,
              })}
              onMouseEnter={e => {
                if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'var(--text)'; }
              }}
              onMouseLeave={e => {
                if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text2)'; }
              }}
            >
              <span style={{ fontSize: '1.25rem', flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
              {expanded && <span style={{ flex: 1 }}>{item.label}</span>}
              {isChat && totalUnread > 0 && (
                <span style={{
                  background: 'var(--accent)', color: '#fff',
                  borderRadius: 50, padding: '1px 5px', fontSize: '0.6rem', fontWeight: 700,
                  minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginLeft: expanded ? 'auto' : undefined,
                  position: expanded ? undefined : 'absolute',
                  top: expanded ? undefined : 5, right: expanded ? undefined : 5,
                }}>
                  {totalUnread > 9 ? '9+' : totalUnread}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Bottom section ── */}
      <div style={{
        padding: '8px', borderTop: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>

        {/* Theme toggle */}
        {expanded ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--text3)', letterSpacing: '0.04em' }}>THEME</span>
            <div style={{ display: 'flex', background: 'var(--bg3)', borderRadius: 20, padding: 3, gap: 2 }}>
              {THEME_OPTS.map(({ icon, val, label }) => (
                <button
                  key={val}
                  onClick={() => setTheme(val)}
                  aria-label={`${label} theme`}
                  aria-pressed={theme === val}
                  title={label}
                  style={{
                    background: theme === val ? 'var(--glow)' : 'transparent',
                    border: `1px solid ${theme === val ? 'var(--accent)' : 'transparent'}`,
                    borderRadius: 14, padding: '3px 8px',
                    fontSize: '0.72rem', cursor: 'pointer',
                    color: theme === val ? 'var(--accent)' : 'var(--text3)',
                    transition: 'all 0.15s',
                  }}
                >{icon}</button>
              ))}
            </div>
          </div>
        ) : (
          <button
            onClick={cycleTheme}
            aria-label={`Current theme: ${theme}. Click to cycle theme.`}
            title={`Theme: ${theme} (click to cycle)`}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              borderRadius: 10, width: '100%', padding: '8px 0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.2rem', transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            {THEME_OPTS[themeIdx]?.icon ?? '⚙️'}
          </button>
        )}

        {/* Profile or guest auth */}
        {currentUser ? (
          <div style={{ position: 'relative' }}>
            {/* User menu popup (expanded only) */}
            {userMenuOpen && expanded && (
              <div className="glass-card" style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
                padding: '6px', zIndex: 50,
              }}>
                <div style={{ padding: '8px 10px 10px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>{currentUser.name}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text3)', marginTop: 2 }}>{currentUser.uniqueId}</div>
                </div>
                {[['🍃 My Profile', '/profile/me'], ['🏆 Leaf Score', '/wallet']].map(([label, path]) => (
                  <button
                    key={path}
                    onClick={() => { navigate(path); setUserMenuOpen(false); }}
                    style={{
                      display: 'block', width: '100%', background: 'none', border: 'none',
                      color: 'var(--text2)', padding: '8px 10px', cursor: 'pointer',
                      textAlign: 'left', fontSize: '0.82rem', borderRadius: 8,
                      fontFamily: 'var(--font-body)', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  >{label}</button>
                ))}
                <button
                  onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }}
                  style={{
                    display: 'block', width: '100%', background: 'none', border: 'none',
                    color: 'var(--color-decline)', padding: '8px 10px', cursor: 'pointer',
                    textAlign: 'left', fontSize: '0.82rem', borderRadius: 8,
                    fontFamily: 'var(--font-body)', borderTop: '1px solid var(--border)',
                    marginTop: 4, transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >Sign Out</button>
              </div>
            )}

            <button
              onClick={() => expanded ? setUserMenuOpen(v => !v) : navigate('/profile/me')}
              aria-label={expanded ? (userMenuOpen ? 'Close account menu' : 'Open account menu') : `Go to ${currentUser.name}'s profile`}
              aria-expanded={expanded ? userMenuOpen : undefined}
              title={!expanded ? currentUser.name : undefined}
              style={{
                width: '100%', padding: expanded ? '8px 10px' : '7px',
                display: 'flex', alignItems: 'center', gap: expanded ? 10 : 0,
                justifyContent: expanded ? 'flex-start' : 'center',
                background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)',
                borderRadius: 10, cursor: 'pointer', color: 'var(--text)',
                fontFamily: 'var(--font-body)', fontSize: '0.8rem',
                whiteSpace: 'nowrap', overflow: 'hidden', transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
            >
              <img
                src={photoUrl(currentUser.photo) || dicebear(currentUser.name)}
                style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                alt=""
                onError={e => { e.target.src = dicebear(currentUser.name); }}
              />
              {expanded && (
                <>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                    {currentUser.name}
                  </span>
                  <span style={{ color: 'var(--text3)', fontSize: '0.65rem', flexShrink: 0 }}>
                    {userMenuOpen ? '▲' : '▼'}
                  </span>
                </>
              )}
            </button>
          </div>
        ) : expanded ? (
          /* Guest: expanded — Sign In + Join */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              onClick={() => navigate('/join?mode=login')}
              className="btn-ghost"
              style={{ width: '100%', padding: '9px', fontSize: '0.82rem' }}
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/join')}
              className="btn-primary"
              style={{ width: '100%', padding: '9px', fontSize: '0.82rem' }}
            >
              Join 🍃
            </button>
          </div>
        ) : (
          /* Guest: collapsed — single join button */
          <button
            onClick={() => navigate('/join')}
            aria-label="Sign in or join WishTree"
            title="Sign In / Join"
            style={{
              background: 'linear-gradient(135deg,var(--accent),var(--accent2))',
              border: 'none', borderRadius: 10, cursor: 'pointer',
              padding: '10px 0', width: '100%', fontSize: '1.2rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >👤</button>
        )}
      </div>
    </div>
  );
}
